/**
 * Bootstrap COF2 v2 - M2.
 *
 * Enregistre les composants Lit, initialise le router + thème, abonne le
 * shell aux changements de route, rend chaque page à la demande.
 */

import './styles/tokens.css';
import './styles/reset.css';
import './styles/layout.css';
import './styles/personnage.css';
import './styles/auth.css';
import './ui/index';
import { authState, initAuth } from '@state/auth';
import { wizardState } from '@state/creation-wizard';
import { isEffectEnabled } from '@state/effects-prefs';
import { applyInitialTheme, themeSignal, toggleTheme } from '@state/ui';
import { html, render } from 'lit';
import { initCursorGlow } from './effects/cursor-glow';
import { initD20 } from './effects/d20';
import { applyAlBhedIfActive, initKonami } from './effects/konami';
import { initMorphText } from './effects/morphtext';
import { initPageTransitions } from './effects/page-transition';
import { initTilt } from './effects/tilt';
import { requestWakeLock } from './effects/wakelock';
import { NAV_LINKS } from './pages/nav-links';
import { renderAccueilPage } from './pages/page-accueil';
import { renderBestiairePage } from './pages/page-bestiaire';
import { renderAventurePage, renderReglesPage, renderUniversPage } from './pages/page-content';
import { renderCreaturePage } from './pages/page-creature';
import { renderDashboardPage } from './pages/page-dashboard';
import { renderEquipementPage } from './pages/page-equipement';
import { renderGlossairePage } from './pages/page-glossaire';
import { renderLoginPage } from './pages/page-login';
import { renderOptionsPage } from './pages/page-options';
import { renderPersonnageFichePage } from './pages/page-personnage-fiche';
import { renderPersonnageListPage } from './pages/page-personnage-list';
import { renderPersonnageNewPage } from './pages/page-personnage-new';
import { renderPeupleDetailPage } from './pages/page-peuple';
import { renderPeuplesPage } from './pages/page-peuples';
import { renderNotFoundPage, renderSoonPage } from './pages/page-placeholder';
import { renderPrestigePage } from './pages/page-prestige';
import { renderProfilDetailPage } from './pages/page-profil';
import { renderProfilsPage } from './pages/page-profils';
import { renderReligionsPage } from './pages/page-religions';
import { type Route, buildHref, initRouter, navigate, routeSignal } from './router';

const appRoot = document.getElementById('app') as HTMLElement | null;
if (!appRoot) throw new Error('#app introuvable');
const root = appRoot;

applyInitialTheme();
initAuth();
initKonami();
if (isEffectEnabled('pageTransitions')) initPageTransitions();
if (isEffectEnabled('cursorGlow')) initCursorGlow();
requestWakeLock();
applyAlBhedIfActive();
initD20(document.body);

function initPageEffects() {
  setTimeout(() => {
    const main = document.querySelector('main.app-main') as HTMLElement | null;
    if (!main) return;
    if (isEffectEnabled('tilt')) initTilt(main);
    // morphText: skip if page already initialized it (morph-container class = in progress)
    const alreadyMorphed = main.querySelector(
      '[data-morph].morph-container, [data-morph].morph-complete',
    );
    if (!alreadyMorphed) {
      if (isEffectEnabled('morphText')) {
        initMorphText(main);
      } else {
        main.querySelectorAll<HTMLElement>('[data-morph]').forEach((el) => {
          el.style.visibility = 'visible';
        });
      }
    }
  }, 100);
}

function renderShell(mainContent: unknown, route: Route): void {
  render(
    html`
      <div class="app-shell">
        <header class="topbar">
          <a class="topbar-brand" href="#/">⚔ COF2</a>
          <nav class="nav" aria-label="Navigation principale">
            ${NAV_LINKS.map((l) => {
              const href = buildHref(l.id);
              const active = route.id === l.id ? 'page' : null;
              return html`<a href=${href} aria-current=${active}><span aria-hidden="true">${l.icon}</span> ${l.label}</a>`;
            })}
          </nav>
          <div style="display: flex; gap: var(--space-2);">
            <button
              class="theme-toggle"
              type="button"
              aria-label="Rechercher (Ctrl/Cmd + K)"
              title="Ctrl/Cmd + K"
              @click=${() => {
                document
                  .querySelector<HTMLElement & { openPanel(): void }>('cof2-search-overlay')
                  ?.openPanel();
              }}
            >
              🔍
            </button>
            <button
              class="theme-toggle"
              type="button"
              aria-label="Changer de thème"
              @click=${toggleTheme}
            >
              ${themeSignal.get() === 'dark' ? '☀' : '☾'}
            </button>
            <a
              class="theme-toggle"
              href="#/options"
              aria-label="Options"
              title="Options"
            >
              ⚙
            </a>
          </div>
        </header>
        <main class="app-main">${mainContent}</main>
        <cof2-toast></cof2-toast>
        <cof2-search-overlay></cof2-search-overlay>
      </div>
    `,
    root,
  );
}

const PUBLIC_ROUTES: string[] = [
  'login',
  'accueil',
  'profils',
  'profil',
  'peuples',
  'peuple',
  'prestige',
  'equipement',
  'bestiaire',
  'creature',
  'compagnons',
  'religions',
  'regles',
  'aventure',
  'univers',
  'glossaire',
  'options',
];

async function renderRoute(route: Route): Promise<void> {
  // Guard auth : si non connecte et route protegee -> login
  const auth = authState.get();
  if (auth.status === 'loading') {
    renderShell(
      html`<p class="muted" style="text-align:center;padding:var(--space-8);">Chargement...</p>`,
      route,
    );
    return;
  }
  if (auth.status === 'unauthenticated' && !PUBLIC_ROUTES.includes(route.id)) {
    navigate('login');
    return;
  }
  if (auth.status === 'authenticated' && route.id === 'login') {
    navigate('dashboard');
    return;
  }
  if (auth.status === 'authenticated' && route.id === 'accueil') {
    navigate('dashboard');
    return;
  }

  // Page login : pas de shell (pas de nav)
  if (route.id === 'login') {
    let loginContent: unknown;
    const rerender = () => {
      loginContent = renderLoginPage(rerender);
      render(loginContent, root);
    };
    loginContent = renderLoginPage(rerender);
    render(loginContent, root);
    return;
  }

  renderShell(html`<p class="muted">Chargement...</p>`, route);

  let content: unknown;
  try {
    switch (route.id) {
      case 'accueil':
        content = await renderAccueilPage();
        break;
      case 'profils':
        content = await renderProfilsPage();
        break;
      case 'profil':
        content = await renderProfilDetailPage(route.params.id ?? '');
        break;
      case 'peuples':
        content = await renderPeuplesPage();
        break;
      case 'peuple':
        content = await renderPeupleDetailPage(route.params.id ?? '');
        break;
      case 'prestige':
        content = await renderPrestigePage();
        break;
      case 'equipement':
        content = await renderEquipementPage();
        break;
      case 'bestiaire':
        content = await renderBestiairePage();
        break;
      case 'creature':
        content = await renderCreaturePage(route.params.id ?? '');
        break;
      case 'religions':
        content = await renderReligionsPage();
        break;
      case 'regles':
        content = await renderReglesPage();
        break;
      case 'aventure':
        content = await renderAventurePage();
        break;
      case 'univers':
        content = await renderUniversPage();
        break;
      case 'glossaire':
        content = await renderGlossairePage();
        break;
      case 'compagnons':
        content = renderSoonPage('Compagnons', 'M3');
        break;
      case 'personnage':
        content = await renderPersonnageListPage();
        break;
      case 'personnage-new':
        content = await renderPersonnageNewPage();
        break;
      case 'personnage-fiche':
        content = await renderPersonnageFichePage(route.params.id ?? '');
        break;
      case 'campagne':
        content = renderSoonPage('Mes campagnes', 'Phase C');
        break;
      case 'campagne-lobby':
        content = renderSoonPage('Lobby campagne', 'Phase C');
        break;
      case 'montee-niveau':
        content = renderSoonPage('Montee de niveau', 'Phase E');
        break;
      case 'table':
        content = renderSoonPage('Table (combat live)', 'M4');
        break;
      case 'mj':
        content = renderSoonPage('Outils MJ', 'M4');
        break;
      case 'options': {
        let optContent: unknown;
        const rerender = () => {
          optContent = renderOptionsPage(rerender);
          renderShell(optContent, route);
        };
        content = renderOptionsPage(rerender);
        break;
      }
      case 'dashboard': {
        const user = authState.get();
        if (user.status === 'authenticated') {
          content = await renderDashboardPage(user.user);
        } else {
          navigate('login');
          return;
        }
        break;
      }
      default:
        content = renderNotFoundPage(route.path);
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    content = html`<h1 class="page-title">Erreur</h1>
      <pre style="white-space: pre-wrap;">${msg}</pre>`;
  }

  renderShell(content, route);
  initPageEffects();
}

routeSignal.subscribe((route) => {
  void renderRoute(route);
});

// Re-rend quand l'etat auth change (loading -> unauthenticated -> authenticated).
authState.subscribe(() => {
  void renderRoute(routeSignal.get());
});

// Re-rend le shell quand le theme change pour mettre a jour l'icone toggle.
themeSignal.subscribe(() => {
  void renderRoute(routeSignal.get());
});

// Re-rend la page wizard quand le state de creation change.
// On ignore les updates qui ne changent que nom/concept (frappe clavier)
// pour eviter de perdre le focus de l'input.
let prevWizardSnap = '';
wizardState.subscribe(() => {
  const route = routeSignal.get();
  if (route.id === 'personnage-new') {
    const s = wizardState.get();
    const snap = JSON.stringify({
      step: s.step,
      profilId: s.profilId,
      isHybride: s.isHybride,
      profilSecondaireId: s.profilSecondaireId,
      profilPrimaireId: s.profilPrimaireId,
      peupleId: s.peupleId,
      caracSerie: s.caracSerie,
      caracAssignment: s.caracAssignment,
      ideaux: s.ideaux,
      defauts: s.defauts,
      phobies: s.phobies,
      secretsIntimes: s.secretsIntimes,
      talentsSecondaires: s.talentsSecondaires,
      humainBonusCarac: s.humainBonusCarac,
      age: s.age,
      taille: s.taille,
      poids: s.poids,
      voiesChoisies: s.voiesChoisies,
      voiePeupleId: s.voiePeupleId,
      useVoieMage: s.useVoieMage,
      languesSupp: s.languesSupp,
      voieRang2: s.voieRang2,
      diviniteId: s.diviniteId,
      choixVoies: s.choixVoies,
      peupleModBonusChoices: s.peupleModBonusChoices,
      peupleModMalusChoices: s.peupleModMalusChoices,
      equipChoices: s.equipChoices,
    });
    if (snap !== prevWizardSnap) {
      prevWizardSnap = snap;
      void renderRoute(route);
    }
  }
});

initRouter();
